import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "dcs-migration-engineering",
  kind: "domain-vertical",
  title: "DCS Migration & Control Engineering",
  shortTitle: "DCS Migrations",
  status: "draft",
  tier: "experience",
  seoTitle: "DCS Migration & Control Engineering | GA",
  seoDescription:
    "Legacy DCS modernizations, Burner Management System replacements, boiler control upgrades, and zero-trip cutover execution for power generation.",
  summary:
    "Legacy DCS modernizations, Burner Management System (BMS) redesigns, auxiliary boiler refurbishments, and zero-trip cutover execution on active generating units.",
  image: "/images/blog/operator-workplaces-hero.webp",
  relatedCapabilitySlugs: [],
  relatedIndustrySlugs: ["energy"],
  relatedPostSlugs: [
    "operator-workplaces",
    "interactive-industrial-schematics",
    "hot-cutover-realities",
  ],
  engineering: [
    "Legacy DCS migration engineering: transitioning ABB Bailey Infi90/Net90 to Emerson Ovation and ABB 800xA",
    "Complete Burner Management System (BMS) redesigns including flame scanners, fuel gas skids, and safety interlocks",
    "Replacement and modernization of vintage 1950s/1960s electromechanical trip bus relays with solid-state DCS logic",
    "Auxiliary boiler refurbishment: replacement instrumentation, I/O list reconciliation, and burner control modernization",
    "AutoCAD controls block diagrams, SAMA logic schematics, P&ID updates, and wiring termination schedules",
    "Phased outage cutover planning, loop-check management, and hot/cold commissioning procedures ensuring zero unplanned trips",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Replacing Vintage Control Systems Without Tripping the Unit",
    },
    {
      type: "paragraph",
      text: "Power generation and heavy industrial facilities often run critical processes on Distributed Control Systems (DCS) installed decades ago. Obsolete components such as ABB Bailey Infi90 modules or mechanical trip bus relays suffer from deteriorating reliability, dwindling spare parts supplies, and vanished engineering expertise. Yet replacing a primary control or burner management system presents extreme risk: a single wiring error or unmapped interlock can trip a multi-hundred-megawatt turbine.",
    },
    {
      type: "heading",
      level: 2,
      text: "Point-by-Point Engineering Rigor Across the Migration Lifecycle",
    },
    {
      type: "paragraph",
      text: "Successful DCS migrations begin with exhaustive pre-outage engineering: walking down running field wiring, reconciling physical I/O terminations against legacy logic drawings, translating proprietary control logic into modern DCS function blocks, and engineering modern operator consoles. By planning every loop check, pre-testing cabinets during factory acceptance, and scripting cutover sequences hour by hour, new control systems come online smoothly within scheduled outage windows.",
    },
  ],
  phases: {
    study: {
      focus:
        "Audit existing legacy DCS hardware, field instrumentation, I/O counts, failure histories, and outage window constraints.",
      produces: [
        "Legacy DCS architecture assessment and obsolescence risk evaluation",
        "Field I/O point inventory and signal type classification (AI, AO, DI, DO, RTD, TC)",
        "Safety interlock and Burner Management System (BMS) regulatory compliance audit",
        "Outage timeline feasibility and cutover staging strategy",
      ],
      fit: {
        good: [
          "Power plants, thermal utilities, and industrial processing facilities running aging DCS (Infi90, WDPF, Provox) facing obsolescence",
          "Facilities executing burner management modernizations, auxiliary boiler upgrades, or control room console replacements",
        ],
        poor: [
          "Greenfield installations with no existing instrumentation, legacy wiring, or active plant cutover constraints",
          "Small discrete packaging machines where standard micro-PLCs suffice without DCS architecture",
        ],
      },
    },
    "initial-design": {
      focus:
        "Develop new DCS system architectures, I/O database mappings, control room HMI layouts, and vendor equipment specifications.",
      produces: [
        "Modern DCS system topology and redundant network architecture designs",
        "Preliminary I/O database cross-referencing legacy points to new DCS terminations",
        "Burner Management System (BMS) safety logic and flame scanner integration architectures",
        "Control room operator console layout and graphic display wireframes",
      ],
    },
    "detailed-design": {
      focus:
        "Produce detailed AutoCAD controls block diagrams, wiring termination drawings, SAMA logic translations, and FAT protocols.",
      produces: [
        "Detailed AutoCAD controls block diagrams, cabinet layouts, and power distribution schematics",
        "Complete wiring termination and marshalling cabinet landing schedules",
        "Translated control logic functional specifications (SAMA and ISA standard logic)",
        "Factory Acceptance Test (FAT) test matrices and simulation test scripts",
      ],
    },
    implementation: {
      focus:
        "Supervise outage installation, execute field loop checks, manage DCS commissioning, and provide startup technical support.",
      produces: [
        "Executed field loop-check verification logs and calibration records",
        "Commissioned and tuned DCS control loops across auxiliary and primary systems",
        "As-built controls drawing packages and updated plant P&IDs",
        "Operator training manuals and engineering maintenance guides",
      ],
    },
  },
};
